import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';

import {
  Save,
  AlertTriangle,
  Check,
  X,
  User as UserIcon,
  Info,
  Search,
  Loader2,
  Trash2,
} from 'lucide-react';

import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { Roles, User, ROLE_DISPLAY } from './userTypes';
import { useAuthStore } from '@/store/authStore';
import { useUser, useUpdateUser, useUpdateUserRole, useUserProjects } from './hooks/useUsers';
import { Loading } from '@/components/ui/loading';
import { useSearchCustomersQuery } from '@/hooks/customerHooks';
import { useSearchCustomerSitesQuery } from '@/hooks/siteHooks';
import { postAddProjectMemberCustomer, removeProjectMember } from '@/app/Sites/Users/SiteUsersApi';
import { useToast } from '@/hooks/use-toast';
function EditSiteAccessModal({
  isOpen,
  onClose,
  onSave,
  existingProjectIds,
  existingByCompany,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (allChanges: { companyId: string; adds: string[]; removes: string[] }[]) => void;
  existingProjectIds: string[];
  existingByCompany: Record<string, number>;
}) {
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  // Persists across company navigation — tracks all checked site IDs globally
  const [checkedSiteIds, setCheckedSiteIds] = useState<Set<string>>(new Set());
  // Tracks which company each site belongs to (learned as we visit companies)
  const [siteCompanyMap, setSiteCompanyMap] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: customerPages,
    isLoading: customersLoading,
    fetchNextPage: fetchNextCustomers,
    hasNextPage: hasMoreCustomers,
    isFetchingNextPage: isFetchingMoreCustomers,
  } = useSearchCustomersQuery({
    query: companySearch,
    enabled: isOpen && !selectedCompanyId,
  });
  const customers = customerPages?.pages.flat() ?? [];

  const { data: sitePages, isLoading: sitesLoading } = useSearchCustomerSitesQuery({
    query: siteSearch,
    customerID: selectedCompanyId || '',
    enabled: isOpen && !!selectedCompanyId,
  });
  const allSites = sitePages?.pages.flat() ?? [];

  // Update siteCompanyMap when sites load for a company
  useEffect(() => {
    if (selectedCompanyId && allSites.length > 0) {
      setSiteCompanyMap((prev) => {
        const next = { ...prev };
        for (const site of allSites) {
          next[site.id] = selectedCompanyId;
        }
        return next;
      });
    }
  }, [selectedCompanyId, allSites]);

  // Initialize checkedSiteIds from existing when modal opens
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen && !prevOpen) {
    setCheckedSiteIds(new Set(existingProjectIds));
    setSiteCompanyMap({});
    setIsSaving(false);
  }
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
  }

  const handleSelectCompany = (companyId: string, companyName: string) => {
    setSelectedCompanyId(companyId);
    setSelectedCompanyName(companyName);
    setSiteSearch('');
  };

  const handleClose = () => {
    setCompanySearch('');
    setSelectedCompanyId(null);
    setSelectedCompanyName('');
    setSiteSearch('');
    setCheckedSiteIds(new Set());
    setSiteCompanyMap({});
    setIsSaving(false);
    onClose();
  };

  const handleToggleSite = (siteId: string) => {
    setCheckedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setCheckedSiteIds((prev) => {
      const next = new Set(prev);
      for (const site of allSites) {
        next.add(site.id);
      }
      return next;
    });
  };

  const handleDeselectAll = () => {
    setCheckedSiteIds((prev) => {
      const next = new Set(prev);
      for (const site of allSites) {
        next.delete(site.id);
      }
      return next;
    });
  };

  // Compute adds/removes for the current company view
  const currentAdds = allSites.filter((s) => checkedSiteIds.has(s.id) && !existingProjectIds.includes(s.id));
  const currentRemoves = allSites.filter((s) => !checkedSiteIds.has(s.id) && existingProjectIds.includes(s.id));

  // Compute total changes across all companies
  const existingSet = new Set(existingProjectIds);
  const totalAdds = [...checkedSiteIds].filter((id) => !existingSet.has(id) && siteCompanyMap[id]);
  const totalRemoves = existingProjectIds.filter((id) => !checkedSiteIds.has(id) && siteCompanyMap[id]);
  const hasTotalChanges = totalAdds.length > 0 || totalRemoves.length > 0;

  const checkedInCurrentCompany = allSites.filter((s) => checkedSiteIds.has(s.id)).length;
  const allVisibleChecked = allSites.length > 0 && allSites.every((s) => checkedSiteIds.has(s.id));

  const formatDiff = (adds: number, removes: number) => {
    const parts = [adds > 0 && `+${adds}`, removes > 0 && `-${removes}`].filter(Boolean);
    return parts.length > 0 ? ` (${parts.join(', ')})` : '';
  };

  const handleSave = () => {
    if (!hasTotalChanges) return;
    setIsSaving(true);

    // Group changes by company
    const changesByCompany: Record<string, { adds: string[]; removes: string[] }> = {};
    for (const siteId of totalAdds) {
      const companyId = siteCompanyMap[siteId];
      if (!companyId) continue;
      if (!changesByCompany[companyId]) changesByCompany[companyId] = { adds: [], removes: [] };
      changesByCompany[companyId].adds.push(siteId);
    }
    for (const siteId of totalRemoves) {
      const companyId = siteCompanyMap[siteId];
      if (!companyId) continue;
      if (!changesByCompany[companyId]) changesByCompany[companyId] = { adds: [], removes: [] };
      changesByCompany[companyId].removes.push(siteId);
    }

    onSave(
      Object.entries(changesByCompany).map(([companyId, { adds, removes }]) => ({
        companyId,
        adds,
        removes,
      }))
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex h-[80vh] max-h-[600px] flex-col border border-gray-800 sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-white">
            {selectedCompanyId ? `Manage Sites — ${selectedCompanyName}` : 'Select Company'}
          </DialogTitle>
        </DialogHeader>

        {!selectedCompanyId ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-1">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
              <Input
                type="search"
                isSearch
                placeholder="Search companies..."
                className="w-full bg-[#1a1a1a] pl-8 text-white"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
              {customersLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-lime-500" />
                </div>
              ) : customers.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {customers.map((customer) => {
                    const count = existingByCompany[customer.id] ?? 0;
                    return (
                      <div
                        key={customer.id}
                        className="flex cursor-pointer items-center justify-between p-3 hover:bg-gray-800/50"
                        onClick={() => handleSelectCompany(customer.id, customer.name)}
                      >
                        <div className="font-semibold text-white">{customer.name}</div>
                        {count > 0 && (
                          <span className="rounded-full bg-lime/10 px-2 py-0.5 text-xs text-lime">
                            {count} {count === 1 ? 'site' : 'sites'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {hasMoreCustomers && (
                    <div className="p-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-white/60 hover:text-white"
                        onClick={() => fetchNextCustomers()}
                        disabled={isFetchingMoreCustomers}
                      >
                        {isFetchingMoreCustomers ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Load More
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-400">
                  {companySearch ? 'No companies found' : 'Start typing to search'}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              {hasTotalChanges && (
                <Button
                  type="button"
                  disabled={isSaving}
                  onClick={handleSave}
                  className="flex-1 bg-lime text-black hover:bg-lime/90"
                >
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save All Changes
                  {formatDiff(totalAdds.length, totalRemoves.length)}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-1">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
              <Input
                type="search"
                isSearch
                placeholder="Search sites..."
                className="w-full bg-[#1a1a1a] pl-8 text-white"
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
              />
            </div>
            {!sitesLoading && allSites.length > 0 && (
              <div className="flex items-center justify-between mx-2">
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">
                  {checkedInCurrentCompany} of {allSites.length} selected
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-lime hover:text-black"
                  onClick={allVisibleChecked ? handleDeselectAll : handleSelectAll}
                >
                  {allVisibleChecked ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
              {sitesLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin text-lime-500" />
                </div>
              ) : allSites.length > 0 ? (
                <div className="divide-y divide-gray-800">
                  {allSites.map((site) => (
                    <label
                      key={site.id}
                      className="flex cursor-pointer items-center gap-3 p-3 hover:bg-gray-800/50"
                    >
                      <Checkbox
                        checked={checkedSiteIds.has(site.id)}
                        onCheckedChange={() => handleToggleSite(site.id)}
                      />
                      <span className="text-white">{site.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-400">
                  {siteSearch ? 'No sites found' : 'No sites available'}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedCompanyId(null);
                  setSelectedCompanyName('');
                  setSiteSearch('');
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={!hasTotalChanges || isSaving}
                onClick={handleSave}
                className="flex-1 bg-lime text-black hover:bg-lime/90"
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
                {hasTotalChanges && ` (${[totalAdds.length > 0 && `+${totalAdds.length}`, totalRemoves.length > 0 && `-${totalRemoves.length}`].filter(Boolean).join(', ')})`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EditUsersPageSimple() {
  const { userId } = useParams();
  const updateUser = useUpdateUser();
  const updateRole = useUpdateUserRole();
  const { roles: userRoles } = useAuthStore();
  const isSuperAdmin = userRoles.includes(Roles.SuperAdmin);
  const isIntegratorAdmin = userRoles.includes(Roles.IntegratorAdmin);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useUser(userId || '');
  const { data: userProjects } = useUserProjects(userId || '');
  const [isAddSiteModalOpen, setIsAddSiteModalOpen] = useState(false);
  const [formData, setFormData] = useState<Omit<User, 'id'>>({
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
  });
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});
  // Reset errors when form data changes
  useEffect(() => {
    setErrors({});
  }, [formData]);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Normalize: if user has multiple Watchful Go roles, keep only the highest
  const normalizeRoles = (rawRoles: string[]): Set<string> => {
    const goRolesRanked = [Roles.CompanyAdmin, Roles.ProjectAdmin, Roles.User];
    const userGoRoles = rawRoles.filter((r) => goRolesRanked.includes(r as Roles));
    const normalized = new Set(rawRoles);
    if (userGoRoles.length > 1) {
      const highest = goRolesRanked.find((r) => userGoRoles.includes(r))!;
      for (const r of goRolesRanked) {
        if (r !== highest) normalized.delete(r);
      }
    }
    return normalized;
  };

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roles: user.roles,
      });
      setSelectedRoles(normalizeRoles(user.roles));
    }
  }, [user]);

  // Track form changes (compare against normalized baseline)
  useEffect(() => {
    if (!user) return;

    const baseline = normalizeRoles(user.roles);
    const currentRoles = Array.from(selectedRoles);
    const hasFormChanged =
      formData.firstName !== user.firstName ||
      formData.lastName !== user.lastName ||
      formData.email !== user.email ||
      currentRoles.length !== baseline.size ||
      !currentRoles.every((role) => baseline.has(role));

    setHasChanges(hasFormChanged);
  }, [formData, selectedRoles, user]);

  const [isRemovingSite, setIsRemovingSite] = useState<string | null>(null);

  const handleSaveSiteAccess = async (allChanges: { companyId: string; adds: string[]; removes: string[] }[]) => {
    if (!userId) return;
    try {
      for (const changes of allChanges) {
        for (const siteId of changes.adds) {
          await postAddProjectMemberCustomer(changes.companyId, siteId, userId);
        }
        for (const siteId of changes.removes) {
          await removeProjectMember(changes.companyId, siteId, userId);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['userProjects', userId] });
      toast({ title: 'Success', description: 'Site access updated' });
      setIsAddSiteModalOpen(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to update site access', variant: 'destructive' });
    }
  };

  const handleRemoveSite = async (companyId: string, siteId: string) => {
    if (!userId) return;
    setIsRemovingSite(siteId);
    try {
      await removeProjectMember(companyId, siteId, userId);
      queryClient.invalidateQueries({ queryKey: ['userProjects', userId] });
      toast({ title: 'Success', description: 'Site access removed' });
    } catch {
      toast({ title: 'Error', description: 'Failed to remove site access', variant: 'destructive' });
    } finally {
      setIsRemovingSite(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-white/70">User not found</div>
      </div>
    );
  }

  const cancelChanges = () => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roles: user.roles,
    });
    setSelectedRoles(normalizeRoles(user.roles));
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetailsChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRoleToggle = (role: Roles) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  };

  const hasImplicitWatchfulGoAccess = selectedRoles.has(Roles.SuperAdmin) || selectedRoles.has(Roles.IntegratorAdmin);

  const WATCHFUL_GO_ROLES_RANKED = [Roles.CompanyAdmin, Roles.ProjectAdmin, Roles.User] as const;

  const selectedWatchfulGoRole = WATCHFUL_GO_ROLES_RANKED.find((r) => selectedRoles.has(r)) ?? null;

  const handleWatchfulGoRoleSelect = (role: Roles) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      // Remove all Watchful Go roles
      for (const r of WATCHFUL_GO_ROLES_RANKED) {
        next.delete(r);
      }
      // Toggle: if same role was selected, leave all removed; otherwise set the new one
      if (role !== selectedWatchfulGoRole) {
        next.add(role);
      }
      return next;
    });
  };

  const canAssignRole = (role: Roles): boolean => {
    if (isSuperAdmin) return true;
    if (isIntegratorAdmin) return role !== Roles.SuperAdmin;
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !user) return;

    if (!validateForm()) {
      return;
    }

    try {
      const newRoles = Array.from(selectedRoles);

      // First, update user info if basic fields changed
      if (
        formData.firstName !== user.firstName ||
        formData.lastName !== user.lastName ||
        formData.email !== user.email
      ) {
        await updateUser.mutateAsync({
          id: userId,
          data: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            roles: newRoles,
          },
        });
      }

      // Then, update roles that have changed
      const rolesToAdd = newRoles.filter((role) => !user.roles.includes(role));
      const rolesToRemove = user.roles.filter((role) => !newRoles.includes(role));

      // Process role changes sequentially
      for (const role of rolesToAdd) {
        await updateRole.mutateAsync({
          userId,
          role,
          enabled: true,
        });
      }

      for (const role of rolesToRemove) {
        await updateRole.mutateAsync({
          userId,
          role,
          enabled: false,
        });
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  return (
    <>
      <div className="">
        {hasChanges && (
          <div
            className="sticky top-0 z-10 mr-2 flex animate-fade-in-down items-center justify-between gap-4 border-b bg-[#111] p-3"
            data-onboarding="user-detail-header"
          >
            <p className="">You have unsaved changes. Please save before leaving</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => cancelChanges()}>
                Cancel
              </Button>

              <Button
                onClick={handleSubmit}
                className="flex items-center gap-2 bg-lime text-black hover:bg-lime/90"
                disabled={updateUser.isPending || updateRole.isPending}
                data-onboarding="user-detail-save-button"
              >
                <Save className="h-4 w-4" />
                {updateUser.isPending || updateRole.isPending ? 'Saving...' : 'Save User'}
              </Button>
            </div>
          </div>
        )}
        <Tabs defaultValue="details" className="w-full">
          <div className="p-6">
            <TabsContent value="details">
              <div
                className="mx-auto mb-4 flex max-w-[1280px] items-center justify-between rounded-lg border bg-card2 p-4"
                data-onboarding="user-detail-info-card"
              >
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-700/20">
                    <UserIcon className="h-6 w-6 text-gray-300" />
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-white">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-sm text-gray-300">{user.email}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-sm text-gray-300">
             
                </div>
              </div>
              <section className="mx-auto grid max-w-[1280px] grid-cols-[320px_1fr] gap-6">
                <div data-onboarding="user-detail-sidebar">
                  <div className="sticky top-36 flex flex-col gap-2 font-semibold text-lime">
                    <a href="#user">User Details</a>
                    <a href="#user">Roles</a>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Basic Information */}
                    <Card className="border-white/10" data-onboarding="user-detail-basic-info">
                      <CardHeader>
                        <CardTitle className="text-white">Basic Information</CardTitle>
                        <CardDescription>User profile and account details</CardDescription>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName" className="text-white">
                            First name
                          </Label>
                          <Input
                            id="firstName"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={errors.firstName ? 'border-red-500' : ''}
                          />
                          {errors.firstName && (
                            <p className="text-sm text-red-500">{errors.firstName}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName" className="text-white">
                            Last name
                          </Label>
                          <Input
                            id="lastName"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className={errors.lastName ? 'border-red-500' : ''}
                          />
                          {errors.lastName && (
                            <p className="text-sm text-red-500">{errors.lastName}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-white">
                            Email
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleDetailsChange('email', e.target.value)}
                            className={errors.email ? 'border-red-500' : ''}
                          />
                          {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Section A: Monitoring Portal */}
                    <Card className="border-white/10" data-onboarding="user-detail-roles">
                      <CardHeader>
                        <CardTitle className="text-white">Monitoring Portal Access</CardTitle>
                        <CardDescription>Access to the monitoring portal for alert handling</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <label className="flex cursor-pointer items-start gap-3">
                          <Checkbox
                            checked={selectedRoles.has(Roles.Monitoring)}
                            onCheckedChange={() => handleRoleToggle(Roles.Monitoring)}
                            disabled={!canAssignRole(Roles.Monitoring)}
                            data-onboarding="user-detail-role-monitoring"
                          />
                          <div>
                            <p className="font-medium text-white">{ROLE_DISPLAY[Roles.Monitoring].label}</p>
                            <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.Monitoring].description}</p>
                          </div>
                        </label>
                      </CardContent>
                    </Card>

                    {/* Section B: Admin Portal */}
                    <Card className="border-white/10" data-onboarding="user-detail-admin-roles">
                      <CardHeader>
                        <CardTitle className="text-white">Admin Portal Access</CardTitle>
                        <CardDescription>Access to the admin portal for system configuration</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* If the viewer is a super admin or the user is already a super admin, show the role */}
                        {(isSuperAdmin || user.roles.includes(Roles.SuperAdmin)) && (
                          <label className="flex cursor-pointer items-start gap-3">
                            <Checkbox
                              checked={selectedRoles.has(Roles.SuperAdmin)}
                              onCheckedChange={() => handleRoleToggle(Roles.SuperAdmin)}
                            />
                            <div>
                              <p className="font-medium text-white">{ROLE_DISPLAY[Roles.SuperAdmin].label}</p>
                              <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.SuperAdmin].description}</p>
                            </div>
                          </label>
                        )}
                        <label className="flex cursor-pointer items-start gap-3">
                          <Checkbox
                            checked={selectedRoles.has(Roles.IntegratorAdmin)}
                            onCheckedChange={() => handleRoleToggle(Roles.IntegratorAdmin)}
                            disabled={!canAssignRole(Roles.IntegratorAdmin)}
                            data-onboarding="user-detail-role-admin"
                          />
                          <div>
                            <p className="font-medium text-white">{ROLE_DISPLAY[Roles.IntegratorAdmin].label}</p>
                            <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.IntegratorAdmin].description}</p>
                          </div>
                        </label>
                        {user.roles.includes(Roles.Installer) && (
                          <label className="flex cursor-pointer items-start gap-3">
                            <Checkbox
                              checked={selectedRoles.has(Roles.Installer)}
                              onCheckedChange={() => handleRoleToggle(Roles.Installer)}
                              disabled={!canAssignRole(Roles.Installer)}
                            />
                            <div>
                              <p className="font-medium text-white">{ROLE_DISPLAY[Roles.Installer].label}</p>
                              <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.Installer].description}</p>
                            </div>
                          </label>
                        )}
                        {user.roles.includes(Roles.SalesPerson) && (
                          <label className="flex cursor-pointer items-start gap-3">
                            <Checkbox
                              checked={selectedRoles.has(Roles.SalesPerson)}
                              onCheckedChange={() => handleRoleToggle(Roles.SalesPerson)}
                              disabled={!canAssignRole(Roles.SalesPerson)}
                            />
                            <div>
                              <p className="font-medium text-white">{ROLE_DISPLAY[Roles.SalesPerson].label}</p>
                              <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.SalesPerson].description}</p>
                            </div>
                          </label>
                        )}
                        {hasImplicitWatchfulGoAccess && (
                          <div className="mt-2 flex items-start gap-2 rounded-md bg-blue-500/10 p-3">
                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                            <p className="text-sm text-blue-400">
                              {selectedRoles.has(Roles.SuperAdmin) ? ROLE_DISPLAY[Roles.SuperAdmin].label : ROLE_DISPLAY[Roles.IntegratorAdmin].label} role includes full Watchful Go access
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Section C: Watchful Go */}
                    <Card className="border-white/10" data-onboarding="user-detail-watchfulgo-roles">
                      <CardHeader>
                        <CardTitle className="text-white">Watchful Go Access</CardTitle>
                        <CardDescription>Mobile app access for arm/disarm and site management</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {hasImplicitWatchfulGoAccess ? (
                          <div className="flex items-start gap-2 rounded-md bg-blue-500/10 p-3">
                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                            <p className="text-sm text-blue-400">
                              This user has access to all sites via their {selectedRoles.has(Roles.SuperAdmin) ? ROLE_DISPLAY[Roles.SuperAdmin].label : ROLE_DISPLAY[Roles.IntegratorAdmin].label} role
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-4">
                              {user.roles.includes(Roles.CompanyAdmin) && (
                                <label className="flex cursor-pointer items-start gap-3">
                                  <Checkbox
                                    checked={selectedWatchfulGoRole === Roles.CompanyAdmin}
                                    onCheckedChange={() => handleWatchfulGoRoleSelect(Roles.CompanyAdmin)}
                                    disabled={!canAssignRole(Roles.CompanyAdmin)}
                                  />
                                  <div>
                                    <p className="font-medium text-white">Company Admin</p>
                                    <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.CompanyAdmin].description}</p>
                                  </div>
                                </label>
                              )}
                              <label className="flex cursor-pointer items-start gap-3">
                                <Checkbox
                                  checked={selectedWatchfulGoRole === Roles.ProjectAdmin}
                                  onCheckedChange={() => handleWatchfulGoRoleSelect(Roles.ProjectAdmin)}
                                  disabled={!canAssignRole(Roles.ProjectAdmin)}
                                />
                                <div>
                                  <p className="font-medium text-white">Site Admin</p>
                                  <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.ProjectAdmin].description}</p>
                                </div>
                              </label>
                              <label className="flex cursor-pointer items-start gap-3">
                                <Checkbox
                                  checked={selectedWatchfulGoRole === Roles.User}
                                  onCheckedChange={() => handleWatchfulGoRoleSelect(Roles.User)}
                                  disabled={!canAssignRole(Roles.User)}
                                />
                                <div>
                                  <p className="font-medium text-white">Basic User</p>
                                  <p className="text-sm text-white/60">{ROLE_DISPLAY[Roles.User].description}</p>
                                </div>
                              </label>
                            </div>

                            {/* Site Access */}
                            <div data-onboarding="user-detail-assignments">
                              <div className="mb-3 flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-white">Site Access</p>
                                  <p className="text-sm text-white/60">The above role applies to all sites this user is assigned to</p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setIsAddSiteModalOpen(true)}
                                >
                                  Edit Site Access
                                </Button>
                              </div>
                              {userProjects?.rows && userProjects.rows.length > 0 ? (
                                <Table>
                                  <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-card2/50">
                                      <TableHead>Company</TableHead>
                                      <TableHead>Site</TableHead>
                                      <TableHead className="w-[60px]" />
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {userProjects.rows.map((project) => (
                                      <TableRow key={`${project.customerId}-${project.projectId}`} className="border-white/10 hover:bg-card2/50">
                                        <TableCell className="font-medium text-white">{project.customerName || '—'}</TableCell>
                                        <TableCell>{project.projectName || '—'}</TableCell>
                                        <TableCell className="text-right">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:bg-red-900/20 hover:text-red-400"
                                            onClick={() => {
                                              if (project.customerId && project.projectId) {
                                                handleRemoveSite(project.customerId, project.projectId);
                                              }
                                            }}
                                            disabled={isRemovingSite === project.projectId}
                                          >
                                            {isRemovingSite === project.projectId ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                              <Trash2 className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <p className="text-sm text-white/60">No sites assigned to this user.</p>
                              )}
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>

                    <EditSiteAccessModal
                      isOpen={isAddSiteModalOpen}
                      onClose={() => setIsAddSiteModalOpen(false)}
                      onSave={handleSaveSiteAccess}
                      existingProjectIds={userProjects?.rows?.map((p) => p.projectId || '') || []}
                      existingByCompany={
                        userProjects?.rows?.reduce<Record<string, number>>((acc, p) => {
                          if (p.customerId) {
                            acc[p.customerId] = (acc[p.customerId] || 0) + 1;
                          }
                          return acc;
                        }, {}) || {}
                      }
                    />
                  </div>
                </form>
              </section>
            </TabsContent>

            <TabsContent value="alerts">
              <div className="mx-auto max-w-[1280px]">
                <Card className="border-white/10">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white">Resolved Alerts</CardTitle>
                        <CardDescription>Alerts this user has processed</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select defaultValue="all">
                          <SelectTrigger className="w-[180px] bg-card2">
                            <SelectValue placeholder="Filter by type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Alerts</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                            <SelectItem value="escalated">Escalated</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm">
                          Export
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead>Alert ID</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>Site</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600/20">
                              <X className="h-4 w-4 text-red-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-27-001</TableCell>
                          <TableCell>Kiwi Retail Group</TableCell>
                          <TableCell>Lambton Quay Store</TableCell>
                          <TableCell>Motion Detected</TableCell>
                          <TableCell>Today, 10:23 AM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600/20">
                              <Check className="h-4 w-4 text-green-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-26-042</TableCell>
                          <TableCell>Wellington Security Ltd</TableCell>
                          <TableCell>Head Office</TableCell>
                          <TableCell>Door Open</TableCell>
                          <TableCell>Yesterday, 8:45 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-600/20">
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-25-018</TableCell>
                          <TableCell>Auckland Properties Group</TableCell>
                          <TableCell>Newmarket Building</TableCell>
                          <TableCell>System Offline</TableCell>
                          <TableCell>2 days ago, 3:12 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600/20">
                              <X className="h-4 w-4 text-red-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-24-099</TableCell>
                          <TableCell>Kiwi Retail Group</TableCell>
                          <TableCell>Queenstown Store</TableCell>
                          <TableCell>Motion Detected</TableCell>
                          <TableCell>3 days ago, 11:05 PM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-card2/50">
                          <TableCell>
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600/20">
                              <Check className="h-4 w-4 text-green-600" />
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">ALT-2025-03-23-027</TableCell>
                          <TableCell>Wellington Security Ltd</TableCell>
                          <TableCell>Warehouse</TableCell>
                          <TableCell>Door Open</TableCell>
                          <TableCell>4 days ago, 7:30 AM</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-white/70">Showing 5 of 42 alerts</div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled>
                          Previous
                        </Button>
                        <Button variant="outline" size="sm">
                          Next
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
}
